import { observable, runInAction } from 'mobx';

import { logger } from '@/lib/logger';

export type SaveEntry = {
  key: string;
  revision: number;
  phase: 'pending' | 'failed';
  retry: () => void;
  error?: unknown;
};

/**
 * Tracks in-flight saves for one domain (journal, meetings). A scheduled save
 * stays unsettled until it succeeds; a failed save stays unsettled with a
 * retry until a retry succeeds. A newer scheduled revision replaces the entry
 * for its key, so an older save can never settle over a newer one.
 */
export function createSaveStateTracker(logLabel: string) {
  const entries = new Map<string, SaveEntry>();
  const latestRevisions = new Map<string, number>();
  const failedSaves = observable.array<SaveEntry>([], { deep: false });

  function schedule(key: string, retry: () => void): SaveEntry {
    const revision = (latestRevisions.get(key) ?? 0) + 1;
    latestRevisions.set(key, revision);

    const previous = entries.get(key);
    if (previous) {
      runInAction(() => {
        const index = failedSaves.indexOf(previous);
        if (index !== -1) failedSaves.splice(index, 1);
      });
    }

    const entry: SaveEntry = { key, revision, phase: 'pending', retry };
    entries.set(key, entry);
    return entry;
  }

  // An older revision cannot clear or overwrite a newer one
  function isLive(entry: SaveEntry): boolean {
    const current = entries.get(entry.key);
    return current !== undefined && current.revision === entry.revision;
  }

  /** Marks a save succeeded: drops the entry and the failed-surface entry */
  function settle(entry: SaveEntry): void {
    if (!isLive(entry)) return;
    entries.delete(entry.key);
    runInAction(() => {
      const index = failedSaves.indexOf(entry);
      if (index !== -1) failedSaves.splice(index, 1);
    });
  }

  /** Marks a save failed for any rejection reason, undefined included */
  function fail(entry: SaveEntry, error: unknown): void {
    if (!isLive(entry)) return;
    entry.phase = 'failed';
    entry.error = error;
    if (!failedSaves.includes(entry)) {
      runInAction(() => failedSaves.push(entry));
    }
  }

  function createRetry(
    key: string,
    save: () => Promise<unknown>
  ): () => Promise<void> {
    const retry = () => {
      const entry = schedule(key, retry);
      return save()
        .then(() => settle(entry))
        .catch((error: unknown) => {
          logger.error(`${logLabel} retry failed`, { error });
          fail(entry, error);
        });
    };
    return retry;
  }

  return {
    schedule,
    settle,
    fail,
    createRetry,
    getEntry: (key: string): SaveEntry | undefined => entries.get(key),
    getUnsettled: (): SaveEntry[] => [...entries.values()],
    getFailed: (): SaveEntry[] => [...failedSaves],
  };
}
